import Vector from "victor";
import { Tool } from "@/wbcontroller/types/Tool";
import ToolType from "@/wbcontroller/types/ToolType";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { ShapeType } from "../types/ShapeType";
import {
  SimplePointerEventHandler,
  SimplePointerEventHandlerDelegate,
} from "../SimplePointerEventHandler";
import AddStandardShapeOperation from "./AddStandardShapeOperation";
import ThrottledUpsertController from "../toolHelpers/ThrottledUpsertController";
import AnimationFrameMoveController from "../toolHelpers/AnimationFrameMoveController";
import { NodeShape } from "./NodeShape";
import { Scope } from "../types/Scope";

export class StampTool implements Tool, SimplePointerEventHandlerDelegate {
  // Populated by bind()
  wb?: WhiteboardControllerInterface;
  eventHandler: SimplePointerEventHandler;
  throttledUpsertController: ThrottledUpsertController;
  animationFrameMoveController: AnimationFrameMoveController;
  toolType = ToolType.Stamp;

  // If true, re-enter placement state after placement
  private image?: {
    url: string;
    size: Vector;
  };

  // New shapes are copied from this object
  shapePrototype: NodeShape = {
    shapeType: ShapeType.Node,
    strokeColor: "rgba(0, 0, 0, 0)",
    strokeWidth: 0,
    fillColor: "rgba(0, 0, 0, 0)",
    pointA: [0, 0],
    pointB: [0, 0],
    text: null,

    // properties below this line are set when drawing begins
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
  };

  // Temporary state during an interaction
  isPointerDown = false;
  isShiftDown = false;
  lastPoint?: Vector;

  // Shape info
  shapeID = "";
  shape?: NodeShape;

  constructor() {
    this.throttledUpsertController = new ThrottledUpsertController(
      (scope: Scope) => {
        if (!this.wb || !this.shape) return;

        this.wb.shapeStore.sendUpsertMany(scope, this.shapeID, this.shape);
      }
    );

    this.animationFrameMoveController = new AnimationFrameMoveController(
      this.onAnimationFrame
    );

    this.eventHandler = new SimplePointerEventHandler();
    // appease typescript
    this.shape = Object.assign({}, this.shapePrototype);
  }

  // Settings passed down from
  // Vuex -> Whiteboard.vue -> WhiteboardController -> this method
  settingsDidChange() {
    /* ignore all settings */
  }

  nextZIndexDidChange(nextZIndex: number) {
    this.shapePrototype.zIndex = nextZIndex;
  }

  prepare(image: { url: string; size: { x: number; y: number } }) {
    if (!this.wb) return;
    this.image = {
      url: image.url,
      size: new Vector(image.size.x, image.size.y),
    };
    this.shapePrototype.imageURL = image.url;

    const point = this.lastPoint || this.wb.camera.origin;

    this.isPointerDown = true;
    this.animationFrameMoveController.onPointerDown(point);

    // Set up pen shape to be used while user is actively drawing
    this.shapeID = this.wb.makeID();
    const origin = this.wb.camera.convertScreenToWorld(new Vector(0, 0));
    const pointA = point
      .clone()
      .subtract(this.image.size.clone().divideScalar(2));
    const pointB = point.clone().add(this.image.size);

    this.shape = Object.assign({}, this.shapePrototype, {
      pointA: pointA.toArray(),
      pointB: pointB.toArray(),
      boundingBoxOrigin: origin.toArray(),
      boundingBoxSize: this.wb.camera
        .convertScreenToWorld(this.wb.camera.viewportSize)
        .subtract(origin)
        .toArray(),
      boundingBoxOriginalSize: this.wb.camera.viewportSize.toArray(),
      aspectRatio: image.size.x / image.size.y,
    });

    // Dispatch event to make things show up
    this.throttledUpsertController.call();
  }

  onPointerDown = (point: Vector) => {
    this.lastPoint = point;
    if (!this.wb || !this.image) return;
    if (!this.shape) this.prepare(this.image);
  };

  onPointerMove = (point: Vector) => {
    this.lastPoint = point;
    this.animationFrameMoveController.onPointerMove(point);
  };

  onAnimationFrame = (point: Vector) => {
    if (!this.wb || !this.shape || !this.image) return;

    // Ignore this frame if mouse hasn't moved
    if (Vector.fromArray(this.shape.pointB).isEqualTo(point)) return;

    const pointA = point
      .clone()
      .subtract(this.image.size.clone().divideScalar(2));
    this.shape.pointA = pointA.toArray() as [number, number];
    this.shape.pointB = pointA.clone().add(this.image.size).toArray() as [
      number,
      number
    ];

    this.throttledUpsertController.call();
  };

  onPointerUp = () => {
    if (!this.wb || !this.shape || !this.isPointerDown || !this.image) {
      this.abort();
      return;
    }
    this.throttledUpsertController.cancel();
    this.isPointerDown = false;
    this.animationFrameMoveController.onPointerUp();

    // Dispatch event to create the new shape

    const center = this.wb.camera.convertScreenToWorld(
      Vector.fromArray(this.shape.pointA)
        .add(Vector.fromArray(this.shape.pointB))
        .divideScalar(2)
    );
    const halfSize = this.image.size.clone().divideScalar(2);
    this.shape.pointA = center.clone().subtract(halfSize).toArray() as [
      number,
      number
    ];
    this.shape.pointB = center.clone().add(halfSize).toArray() as [
      number,
      number
    ];

    const newShapeID = this.wb.makeID();
    const op = new AddStandardShapeOperation(
      newShapeID,
      this.wb.camera,
      this.shape,
      true
    );
    this.wb.applyOperation(op);

    // Dispatch events to delete the live shape
    this.wb.shapeStore.sendDelete(Scope.Live, this.shapeID);

    this.shapeID = "";

    if (this.isShiftDown) {
      this.prepare(this.image);
    } else {
      this.image = undefined;
    }
  };

  abort() {
    this.isPointerDown = false;

    this.animationFrameMoveController.abort();
    this.throttledUpsertController.cancel();

    this.wb?.shapeStore.sendDelete(Scope.Live, this.shapeID);

    this.shapeID = "";
    this.shape = undefined;
  }

  setIsShiftDown(isShiftDown: boolean) {
    this.isShiftDown = isShiftDown;
  }

  bind(wb: WhiteboardControllerInterface) {
    this.wb = wb;
    this.eventHandler.bind(wb, this);
  }

  unbind() {
    this.wb = undefined;
    this.eventHandler.unbind();
  }
}
